/**
 * Bridges the scheduler's abstract `TileLoader` to the elevation worker pool.
 *
 * This is the only place that knows a tile is fetched over HTTP by a worker;
 * the scheduler above it and the mesh builder below it both stay testable
 * without either.
 */

import type { TerrainMeshData } from '../core/build/terrainmesh.js';
import type { GeoOrigin } from '../core/geo/enu.js';
import type { TileKey } from '../core/geo/tilekey.js';
import type { ElevationSource } from '../core/sources/types.js';
import { clampToSourceZoom, expandTileUrl } from '../core/sources/types.js';
import type { DesiredTile } from '../core/stream/lod.js';
import type { TileLoader } from '../core/stream/scheduler.js';
import type { ElevationJob } from '../workers/protocol.js';
import { WorkerPool } from '../workers/pool.js';

export class ElevationTileLoader implements TileLoader<TerrainMeshData> {
  private readonly pool: WorkerPool<ElevationJob, TerrainMeshData>;
  private readonly source: ElevationSource;
  private readonly getOrigin: () => GeoOrigin;

  constructor(
    source: ElevationSource,
    getOrigin: () => GeoOrigin,
    workers: number,
    /** Day of year at request time, so tiles are built for the season. */
    private readonly getDay: () => number = () => 172,
    /** The near terrain as flat facets, made in the worker. See `TerrainMeshData.facets`. */
    private readonly facets = false,
  ) {
    this.source = source;
    this.getOrigin = getOrigin;
    this.pool = new WorkerPool<ElevationJob, TerrainMeshData>(
      () =>
        new Worker(new URL('../workers/elevation.worker.ts', import.meta.url), {
          type: 'module',
        }),
      workers,
    );
  }

  /** Whether the overlay has a tile, from its coverage list; fetched once. */
  private coverage: Promise<Coverage> | undefined;

  private overlayTiles(): Promise<Coverage> {
    const index = this.source.overlay?.index;
    const none: Coverage = { has: () => false, hasTile: () => false };
    if (index === undefined) return Promise.resolve(none);
    this.coverage ??= fetch(index)
      .then(r => (r.ok ? r.json() : {}) as Promise<CoverageIndex>)
      .then(coverageOf)
      // Without the list, no overlay: the source's own tiles everywhere.
      .catch(() => none);
    return this.coverage;
  }

  async load(tile: DesiredTile, signal: AbortSignal): Promise<TerrainMeshData> {
    const sourceKey = clampToSourceZoom(tile.key, this.source.minZoom, this.source.maxZoom);
    const overlay = this.source.overlay;
    // At its own zoom and, where it has a pyramid, the coarser ones: in
    // Sweden the far terrain is the laser model's too, averaged.
    const covered = overlay !== undefined && sourceKey.z <= overlay.zoom && sourceKey.z === tile.key.z ? await this.overlayTiles() : undefined;
    const inOverlay = (key: TileKey | undefined) => key !== undefined && covered !== undefined && covered.hasTile(key);
    const around = neighbourKeys(sourceKey);
    const job: ElevationJob = {
      key: tile.key,
      sourceKey,
      url: expandTileUrl(this.source.urlTemplate, sourceKey),
      neighbourUrls: sourceKey.z === tile.key.z ? neighbourUrls(this.source.urlTemplate, sourceKey) : undefined,
      // Only where the coverage list has the tile: elsewhere each would cost a
      // request that finds nothing (and a static host answers with its page).
      ...(overlay !== undefined && covered !== undefined && (inOverlay(sourceKey) || around.some(inOverlay)) ? {
        overlayUrl: inOverlay(sourceKey) ? expandTileUrl(overlay.urlTemplate, sourceKey) : undefined,
        neighbourOverlayUrls: around.map(k => (inOverlay(k) ? expandTileUrl(overlay.urlTemplate, k!) : undefined)),
      } : {}),
      encoding: this.source.encoding,
      tileSize: this.source.tileSize,
      gridRes: tile.gridRes,
      skirtDepth: tile.skirtDepth,
      // Read the origin at request time, not at construction: a rebase between
      // requests must apply to everything started after it.
      origin: this.getOrigin(),
      dayOfYear: this.getDay(),
      facets: this.facets,
    };
    return this.pool.run(job, [], signal);
  }

  dispose(): void {
    this.pool.dispose();
  }
}

/** North, south, west, east, north-west, north-east, south-west, south-east: wrapped round east to west, none past the poles. */
const AROUND: ReadonlyArray<readonly [number, number]> = [[0, -1], [0, 1], [-1, 0], [1, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]];

export function neighbourUrls(template: string, key: TileKey): Array<string | undefined> {
  return neighbourKeys(key).map(k => (k === undefined ? undefined : expandTileUrl(template, k)));
}

/** A tile's eight neighbours, in `AROUND`'s order. */
export function neighbourKeys(key: TileKey): Array<TileKey | undefined> {
  const n = 2 ** key.z;
  return AROUND.map(([dx, dy]) => {
    const y = key.y + dy;
    if (y < 0 || y >= n) return undefined;
    return { z: key.z, x: (key.x + dx + n) % n, y };
  });
}

/**
 * An overlay's coverage list: either every tile, "z/x/y" (`tools/lmdem.mjs`),
 * or, for all of Sweden, a 64 x 64 bitmap of the zoom-`zoom` tiles in each
 * zoom-`blockZoom` block, base64, bit (y % 64) * 64 + (x % 64), low bit first
 * (`tools/lmdem/cli.mjs index`).
 */
export interface CoverageIndex {
  tiles?: string[] | number;
  version?: number;
  zoom?: number;
  blockZoom?: number;
  blocks?: Record<string, string>;
  /**
   * The coarsest zoom the overlay has a pyramid down to (`tools/lmdem/cli.mjs
   * pyramid`): a tile there for every tile with one of `zoom`'s under it.
   */
  minZoom?: number;
}

export interface Coverage {
  /** Whether the list has this tile, "z/x/y", at its own zoom. */
  has(key: string): boolean;
  /**
   * Whether the overlay has a tile at `key`: at its own zoom, one the list
   * has; coarser, down to its pyramid's `minZoom`, one with any of them under it.
   */
  hasTile(key: TileKey): boolean;
}

export function coverageOf(index: CoverageIndex): Coverage {
  const zoom = index.zoom ?? 14;
  const minZoom = Math.min(zoom, index.minZoom ?? zoom);
  if (Array.isArray(index.tiles)) {
    const set = new Set(index.tiles);
    // Every tile of the pyramid: each listed tile's parents, down to minZoom.
    const pyramid = new Set<string>();
    for (const t of set) {
      const [z, x, y] = t.split('/').map(Number);
      for (let k = 0; z - k >= minZoom; k++) pyramid.add(`${z - k}/${x >> k}/${y >> k}`);
    }
    return { has: (key) => set.has(key), hasTile: (k) => pyramid.has(`${k.z}/${k.x}/${k.y}`) };
  }
  const blockZoom = index.blockZoom ?? 8, shift = zoom - blockZoom, side = 1 << shift;
  const blocks = new Map<string, Uint8Array>();
  for (const [k, b64] of Object.entries(index.blocks ?? {})) blocks.set(k, Uint8Array.from(atob(b64), c => c.charCodeAt(0)));
  const bitAt = (x: number, y: number) => {
    const bits = blocks.get(`${x >> shift}/${y >> shift}`);
    if (bits === undefined) return false;
    const bit = (y & (side - 1)) * side + (x & (side - 1));
    return (bits[bit >> 3] & (1 << (bit & 7))) !== 0;
  };
  return {
    has(key) {
      const [z, x, y] = key.split('/').map(Number);
      return z === zoom && bitAt(x, y);
    },
    hasTile(k) {
      if (k.z > zoom || k.z < minZoom) return false;
      if (k.z === zoom) return bitAt(k.x, k.y);
      // The square of finest tiles under it: within one block from blockZoom
      // up; coarser, the whole of each block it spans.
      const d = zoom - k.z;
      if (k.z < blockZoom) {
        const b = blockZoom - k.z;
        for (let bx = k.x << b; bx < (k.x + 1) << b; bx++) {
          for (let by = k.y << b; by < (k.y + 1) << b; by++) if (blocks.get(`${bx}/${by}`)?.some(v => v !== 0)) return true;
        }
        return false;
      }
      for (let x = k.x << d; x < (k.x + 1) << d; x++) {
        for (let y = k.y << d; y < (k.y + 1) << d; y++) if (bitAt(x, y)) return true;
      }
      return false;
    },
  };
}
